import React, {ReactNode} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faImage, faStar} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";
import {getMacaulayAssetUrl} from "../helpers/macaulayLibrary";
import './observation-card.scss';

const DEFAULT_BIRD_ICON = '/assets/fallback-bird.svg';

export type ObservationCardLink = {
    label: string,
    to: string
}

export type ObservationCardProps = {
    kicker?: string|ReactNode,
    title?: string|ReactNode,
    copy?: string|ReactNode,
    image?: string
    badges?: string[],
    familyLink?: ObservationCardLink,
    sourceUrl?: string,
    macaulayCatalogNumbers?: string[]
}

const getBadgeClassName = (badge: string) => {
    const modifier = badge.toLowerCase().replace(/\s+/g, '-');
    return `observation-card__badge observation-card__badge--${modifier}`;
}

export function ObservationCard({kicker, title, copy, image, badges = [], familyLink, sourceUrl, macaulayCatalogNumbers = []}: ObservationCardProps) {
    const isFallbackImage = !image || image.endsWith(DEFAULT_BIRD_ICON);
    const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (event.currentTarget.src.endsWith(DEFAULT_BIRD_ICON)) return;

        event.currentTarget.src = DEFAULT_BIRD_ICON;
        event.currentTarget.classList.add('observation-card__img-element--fallback');
    }

    const renderBadgeContent = (badge: string) => (
        <>
            {badge.toLowerCase() === 'lifer' ?
                <FontAwesomeIcon icon={faStar} aria-hidden="true" />
                : null
            }
            {badge}
        </>
    );

    return (
        <div className="observation-card">
            <div className="observation-card__img">
                <img
                    src={image || DEFAULT_BIRD_ICON}
                    alt={typeof title === 'string' ? title : ''}
                    className={isFallbackImage ? 'observation-card__img-element observation-card__img-element--fallback' : 'observation-card__img-element'}
                    onError={handleImageError}
                />
                <div className="observation-card__image-content">
                    {sourceUrl ?
                        <a className="observation-card__source-link" href={sourceUrl} target="_blank" rel="noreferrer">eBird</a>
                        : null
                    }
                </div>
            </div>
            {(kicker || title || copy) ?
                <div className="observation-card__body">
                    {kicker ?
                        <div className="observation-card__kicker">{kicker}</div>
                        : null
                    }
                    {familyLink ?
                        <Link className="observation-card__family-link" to={familyLink.to}>
                            {familyLink.label}
                        </Link>
                        : null
                    }
                    {title ?
                        <div className="observation-card__title">{title}</div>
                        : null
                    }
                    {copy ?
                        <div className="observation-card__copy">{copy}</div>
                        : null
                    }
                    {macaulayCatalogNumbers.length ?
                        <section className="observation-card__photos" aria-label="Photos">
                            <h3>Photos</h3>
                            <ul>
                                {macaulayCatalogNumbers.map((catalogNumber, index) => (
                                    <li key={`${catalogNumber}-${index}`}>
                                        <a
                                            href={getMacaulayAssetUrl(catalogNumber)}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <FontAwesomeIcon icon={faImage} aria-hidden="true" />
                                            <span>ML{catalogNumber}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </section>
                        : null
                    }
                </div> : null
            }
            {badges.length ?
                <div className="observation-card__badges" aria-label="Observation highlights">
                    {badges.map(badge => (
                        <span className={getBadgeClassName(badge)} key={badge}>
                            {renderBadgeContent(badge)}
                        </span>
                    ))}
                </div>
                : null
            }
        </div>
    )
}
